
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useScrollSnap } from '../../hooks/useScrollSnap';
import { useRemountOnPageHidden } from '../../hooks/useRemountOnPageHidden';
import {
  IonContent,
  IonRefresherContent,
  IonPage,
  IonBackButton,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonSearchbar,
  IonButton,
  IonIcon,
  IonSkeletonText,
  IonFab,
  IonFabButton,
  useIonRouter,
  IonGrid,
  IonText,
  IonRow,
  IonCol,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  useIonViewWillLeave,
  IonChip,
  useIonViewDidEnter,
  IonModal,
} from '@ionic/react';
import {
  call, mail, chatbubble, trash, filter, swapHorizontal,
  peopleOutline, timeOutline, pricetag, searchOutline,
  chevronBack, closeOutline, personAddOutline
} from 'ionicons/icons';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { ContactListItem, Label } from '@rsmaids/shared/types/contacts';
import styles from './ContactsList.module.scss';
import SheetModal from '../../components/SheetModal';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import PositionedRefresher from '../../components/PositionedRefresher';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { getFirestore, collection, onSnapshot, getDocsFromServer } from 'firebase/firestore';
import { getFirebaseApp } from '@rsmaids/shared/services/firebaseApp';
import BasePageWithHeader, { TitleCard, } from '../../components/BasePageWithHeader';
import { useKeyboard } from '@/contexts/keyboardUtils';
import applyOverlayPosition from '../../utils/overlayPosition';
import { placePhoneCall } from '../../utils/placePhoneCall';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../../hooks/useKeyboardHeight';
import NewContact from './NewContact';
import { useTranslation } from 'react-i18next';

const ContactsList: React.FC = () => {
  const { t: tr } = useTranslation();
  const ionRouter = useIonRouter();
  const { setKeyboardHeight } = useKeyboard();

  // Refs for scroll animation
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);

  // Refs for PositionedRefresher (not for scroll animation)
  const titleCardElementRef = useRef<HTMLDivElement>(null);
  const contentElementRef = useRef<HTMLIonGridElement>(null);

  const [contacts, setContacts] = useState<ContactListItem[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<ContactListItem[]>([]);

  // Filter state - always starts clear
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedLabelId, setSelectedLabelId] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [availableLabels, setAvailableLabels] = useState<Label[]>([]);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const fabRef = useRef<HTMLElement | null>(null);

  const overlayRef = useRef<HTMLElement | null>(null);
  // overlayStyle removed — we now set CSS variables on the overlay element
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);

  const contactService = DataServiceFactory.getService();

  // Helper: always keep a pre-sorted contacts array to avoid sorting during render
  const sortContacts = (list: ContactListItem[]) => {
    return [...list].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  };

  // Keyboard tracking using official Ionic approach with transform: translate3d()
  // Per https://ionic.io/blog/keyboard-improvements-for-ionic-apps
  useEffect(() => {
    if (showNewContactModal) {
      [fabRef.current, overlayRef.current].forEach(el => {
        if (el) {
          el.style.removeProperty('transform');
          el.style.removeProperty('bottom');
        }
      });
      setKeyboardHeight(0);
      return;
    }

    let bottomAnimRaf = 0;
    let currentBottom = 0;
    let didShowHandle: { remove: () => Promise<void> } | null = null;
    let didHideHandle: { remove: () => Promise<void> } | null = null;

    const getKeyboardHeightFallback = () => {
      const vv = window.visualViewport;
      if (!vv) return 0;
      const offset = window.innerHeight - vv.height - vv.offsetTop;
      return offset > 50 ? offset : 0;
    };

    const setBottomOffset = (offset: number) => {
      const elements = [fabRef.current, overlayRef.current].filter(Boolean) as HTMLElement[];
      if (offset > 0) {
        const bottomPx = `${offset}px`;
        elements.forEach(el => {
          el.style.setProperty('bottom', bottomPx, 'important');
          el.style.removeProperty('transform');
        });
      } else {
        elements.forEach(el => {
          el.style.removeProperty('bottom');
          el.style.removeProperty('transform');
        });
      }
    };

    const animateBottomTo = (kbHeight: number, forceHide = false) => {
      const effectiveHeight = forceHide ? 0 : (kbHeight > 0 ? kbHeight : getKeyboardHeightFallback());
      const targetBottom = effectiveHeight > 0 ? effectiveHeight + KEYBOARD_GAP_PX : 0;

      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);

      const startBottom = currentBottom;
      const delta = targetBottom - startBottom;
      if (Math.abs(delta) < 1) {
        currentBottom = targetBottom;
        setBottomOffset(currentBottom);
        return;
      }

      const duration = targetBottom > startBottom ? 220 : 170;
      const startTime = performance.now();

      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        currentBottom = startBottom + (delta * eased);
        setBottomOffset(currentBottom);

        if (t < 1) {
          bottomAnimRaf = requestAnimationFrame(step);
        } else {
          currentBottom = targetBottom;
          setBottomOffset(currentBottom);
        }
      };

      bottomAnimRaf = requestAnimationFrame(step);
    };

    // PRIMARY: Use Capacitor keyboard events to START animation immediately
    try {
      Keyboard.addListener('keyboardWillShow', (info) => {
        animateBottomTo(info.keyboardHeight || 0);
      }).then(handle => { didShowHandle = handle; }).catch(() => { });

      Keyboard.addListener('keyboardDidShow', (info) => {
        animateBottomTo(info.keyboardHeight || 0);
      }).catch(() => { });

      Keyboard.addListener('keyboardWillHide', () => {
        animateBottomTo(0, true);
      }).then(handle => { didHideHandle = handle; }).catch(() => { });

      Keyboard.addListener('keyboardDidHide', () => {
        animateBottomTo(0, true);
      }).catch(() => { });
    } catch { }

    // Fallback: visualViewport-based keyboard detection.
    // Keep this enabled for Android too because some device/WebView combinations
    // report delayed or zero keyboardHeight in plugin callbacks.
    const vv = window.visualViewport;
    let vvRaf = 0;
    const handleVVResize = () => {
      if (vvRaf) cancelAnimationFrame(vvRaf);
      vvRaf = requestAnimationFrame(() => {
        if (!vv) return;
        const offset = window.innerHeight - vv.height;
        const kbHeight = offset > 50 ? offset : 0;
        animateBottomTo(kbHeight);
      });
    };
    if (vv) vv.addEventListener('resize', handleVVResize);

    // Re-sync position when app regains focus (e.g. after screen lock/unlock).
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') setTimeout(handleVVResize, 300);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup transforms
      [fabRef.current, overlayRef.current].forEach(el => {
        if (el) { el.style.removeProperty('transform'); el.style.removeProperty('bottom'); }
      });
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      if (vvRaf) cancelAnimationFrame(vvRaf);
      if (vv) vv.removeEventListener('resize', handleVVResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      try { didShowHandle?.remove(); } catch { }
      try { didHideHandle?.remove(); } catch { }
      setKeyboardHeight(0);
    };
  }, [setKeyboardHeight, showNewContactModal]);
  const getFilterOptions = () => {
    if (!contacts.length) return [];

    let recentCount = 0;
    let emailSubscriberCount = 0;
    let smsSubscriberCount = 0;
    const clientTypeCounts = new Map<string, number>();
    const labelCounts = new Map<string, number>();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    contacts.forEach(contact => {
      const { clientType } = contact.contactfilter?.[0] || {};
      if (contact.createdAt) {
        const creationDate = new Date(contact.createdAt);
        if (creationDate > thirtyDaysAgo) recentCount++;
      }
      if (Array.isArray(clientType)) {
        clientType.forEach(type => {
          clientTypeCounts.set(type, (clientTypeCounts.get(type) || 0) + 1);
        });
      }
      if (Array.isArray(contact.labels)) {
        contact.labels.forEach(label => {
          labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
        });
      }
      if (contact.emailSubscribed) emailSubscriberCount++;
      if (contact.smsSubscribed) smsSubscriberCount++;
    });

    const options = [
      {
        id: 'all',
        label: tr('pages.bookingSystem.ContactsList.allContacts'),
        icon: peopleOutline,
        count: contacts.length,
        chipColor: 'primary',
        onClick: () => {
          setActiveFilter('all');
          setSelectedLabelId('');
          setIsFilterModalOpen(false);
        }
      },
      {
        id: 'recent',
        label: tr('pages.bookingSystem.ContactsList.recentContacts'),
        icon: timeOutline,
        count: recentCount,
        chipColor: 'primary',
        onClick: () => {
          setActiveFilter('recent');
          setSelectedLabelId('');
          setIsFilterModalOpen(false);
        }
      },
      {
        id: 'email-subscribers',
        label: tr('pages.bookingSystem.ContactsList.emailSubscribers'),
        icon: mail,
        count: emailSubscriberCount,
        chipColor: 'tertiary shade',
        onClick: () => {
          setActiveFilter('email-subscribers');
          setSelectedLabelId('');
          setIsFilterModalOpen(false);
        }
      },
      {
        id: 'sms-subscribers',
        label: tr('pages.bookingSystem.ContactsList.smsSubscribers'),
        icon: chatbubble,
        count: smsSubscriberCount,
        chipColor: 'tertiary shade',
        onClick: () => {
          setActiveFilter('sms-subscribers');
          setSelectedLabelId('');
          setIsFilterModalOpen(false);
        }
      }
    ];

    Array.from(clientTypeCounts.entries()).forEach(([type, count]) => {
      let chipColor = 'primary';
      if (type === 'blocked members') {
        chipColor = 'danger';
      } else if (type === 'pending members') {
        chipColor = 'warning';
      } else if (type === 'members') {
        chipColor = 'success';
      }
      options.push({
        id: type,
        label: type.charAt(0).toUpperCase() + type.slice(1),
        icon: peopleOutline,
        count,
        chipColor,
        onClick: () => {
          setActiveFilter(type);
          setSelectedLabelId('');
          setIsFilterModalOpen(false);
        }
      });
    });

    availableLabels.forEach(label => {
      const count = labelCounts.get(label.id) || 0;
      if (count > 0) {
        options.push({
          id: label.id,
          label: label.name,
          icon: pricetag,
          count,
          chipColor: 'primary',
          onClick: () => {
            setActiveFilter('label');
            setSelectedLabelId(label.id);
            setIsFilterModalOpen(false);
          }
        });
      }
    });

    return options;
  };

  // Add this debugging function
  const handleFilterClick = () => {
    setIsFilterModalOpen(true);
  };

  // Handle scroll for header animation
  const [refresherDisabled, setRefresherDisabled] = useState(false);
  const wasAtTop = useRef(true);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentElementRef, 66, applyProgress);

  // Compute horizontal position for search overlay (vertical is CSS-driven)
  const updateOverlayPosition = useCallback(() => {
    try {
      applyOverlayPosition(fabRef.current, overlayRef.current, isSearchVisible);
    } catch { }
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  // Re-position overlay horizontally when expanded state changes
  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  useIonViewWillLeave(() => {
    // Only clear filters if NOT going to contact details
    const goingToContactDetail = sessionStorage.getItem('goingToContactDetail');
    if (!goingToContactDetail) {
      setSearchText('');
      setActiveFilter('all');
      setSelectedLabelId('');
    }

    // Clean up flags
    sessionStorage.removeItem('goingToContactDetail');
  });

  // The list is rebuilt while the page is hidden, so it is back at the top on return — except
  // when going into a contact, which keeps the list where it was (the flag is set before leaving)
  const scrollResetKey = useRemountOnPageHidden(
    contentElementRef,
    () => sessionStorage.getItem('goingToContactDetail') !== null
  );
  // The header's collapsed/expanded look is driven by scroll events, and a rebuilt list at
  // scroll 0 fires none — so put the header back to expanded (title card shown, small title
  // hidden) explicitly, otherwise both show at once.
  useEffect(() => {
    applyProgress(0, false);
    wasAtTop.current = true;
  }, [scrollResetKey, applyProgress]);

  useIonViewDidEnter(() => {
    // Clear the refresh flag — Firestore subscription keeps contacts current automatically
    localStorage.removeItem('contactListNeedsRefresh');
  });

  // Subscribe to Firestore contacts — server-first to bypass stale local cache
  useEffect(() => {
    const db = getFirestore(getFirebaseApp(), 'contacts');

    const mapSnap = (snap: import('firebase/firestore').QuerySnapshot): ContactListItem[] =>
      snap.docs.map(d => {
        const data = d.data() as Record<string, unknown>;
        const firstName = (data['firstName'] as string) || '';
        const lastName = (data['lastName'] as string) || '';
        // Wix sync writes the literal string "Unknown" for contacts with no
        // first/last name — treat it as absent so email-only contacts fall
        // back to their email prefix instead of grouping under "U".
        const rawName = (data['name'] as string) || '';
        const syncedName = rawName && rawName !== 'Unknown' ? rawName : '';
        return {
          id: d.id,
          _id: d.id,
          firstName,
          lastName,
          displayName: syncedName || `${firstName} ${lastName}`.trim() || (data['email'] ? String(data['email']).split('@')[0] : ''),
          primaryEmail: (data['email'] as string) || '',
          primaryPhone: (data['phone'] as string) || '',
          company: data['company'] as string | undefined,
          avatar: (data['avatar'] as string) || '',
          labels: Array.isArray(data['labels']) ? data['labels'] as string[] : [],
          labelIds: Array.isArray(data['labels']) ? data['labels'] as string[] : [],
          emailSubscribed: (data['emailSubscribed'] as boolean) ?? false,
          smsSubscribed: (data['smsSubscribed'] as boolean) ?? true,
          zohoContactId: (data['zohoContactId'] as string) || undefined,
          customerCode: (data['customerCode'] as string) || undefined,
        };
      });

    const col = collection(db, 'contacts');

    // Force a server fetch first — bypasses Firestore local cache entirely
    getDocsFromServer(col)
      .then(snap => {
        const sorted = sortContacts(mapSnap(snap));
        // Set contacts and filteredContacts together to avoid an intermediate empty-list render
        setContacts(sorted);
        setFilteredContacts(sorted);
        setIsLoading(false);
        setHasLoadedOnce(true);
        localStorage.setItem('cachedContacts', JSON.stringify(sorted));
        localStorage.setItem('contactsLastLoadTime', Date.now().toString());
      })
      .catch(() => setIsLoading(false));

    // Then keep listening for real-time changes
    const unsub = onSnapshot(col, (snap) => {
      const sorted = sortContacts(mapSnap(snap));
      setContacts(sorted);
      setFilteredContacts(sorted);
      localStorage.setItem('cachedContacts', JSON.stringify(sorted));
      localStorage.setItem('contactsLastLoadTime', Date.now().toString());
    }, () => {});

    return () => unsub();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      // Labels only — loading state is owned by the Firestore server fetch
      try {
        const cachedLabelsJson = localStorage.getItem('cachedLabels');
        if (cachedLabelsJson) {
          try { if (isMounted) setAvailableLabels(JSON.parse(cachedLabelsJson)); } catch { /* ignore */ }
        }
        const labelsResult = await contactService.labels.getAllLabels().catch(() => null);
        if (isMounted && labelsResult && labelsResult.length > 0) {
          setAvailableLabels(labelsResult);
          localStorage.setItem('cachedLabels', JSON.stringify(labelsResult));
        }
      } catch (error) {
        console.error('Error in initializeData:', error);
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [contactService.labels]);

  // Filtering contacts is a pure synchronous derivation of the inputs
  // below, computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as setState calls inside an effect.
  const contactsFilterKey = { contacts, activeFilter, searchText, selectedLabelId };
  const [prevContactsFilterKey, setPrevContactsFilterKey] = useState(contactsFilterKey);
  if (
    prevContactsFilterKey.contacts !== contactsFilterKey.contacts ||
    prevContactsFilterKey.activeFilter !== contactsFilterKey.activeFilter ||
    prevContactsFilterKey.searchText !== contactsFilterKey.searchText ||
    prevContactsFilterKey.selectedLabelId !== contactsFilterKey.selectedLabelId
  ) {
    setPrevContactsFilterKey(contactsFilterKey);

    // Enhanced search: match name, email, or phone
    const lowerSearch = searchText.trim().toLowerCase();
    let filtered = contactService.contacts.getFilteredContacts({
      contacts,
      searchText,
      activeFilter,
      selectedLabelId
    });
    if (lowerSearch) {
      filtered = filtered.filter(contact => {
        const name = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
        const displayName = (contact.displayName || '').toLowerCase();
        const email = (contact.primaryEmail || '').toLowerCase();
        const phone = (contact.primaryPhone || '').toLowerCase();
        return (
          name.includes(lowerSearch) ||
          displayName.includes(lowerSearch) ||
          email.includes(lowerSearch) ||
          phone.includes(lowerSearch)
        );
      });
    }
    setFilteredContacts(filtered);
    setVisibleCount(50);
  }

  const handleContactClick = (contact: ContactListItem) => {
    // Set flag to indicate we're going to contact details
    sessionStorage.setItem('goingToContactDetail', 'true');
    DataStorageService.storeData(`contact-${contact.id}`, contact);
    ionRouter.push(`/tabs/contact/${contact.id}`, 'forward');
  };

  const handleAddContact = () => {
    setShowNewContactModal(true);
  };

  const handleRefresh = async (event: CustomEvent) => {
    try {
      setIsLoading(true); // Add this to show skeleton during refresh
      const [contactsResult, labelsResult] = await Promise.allSettled([
        contactService.contacts.getAllContacts(),
        contactService.labels.getAllLabels()
      ]);

      if (contactsResult.status === 'fulfilled') {
        const rawContacts = contactsResult.value;

        // Get subscription data for all contacts
        const allEmails = rawContacts.map(c => c.primaryEmail).filter(Boolean);
        const allPhones = rawContacts.map(c => c.primaryPhone).filter(Boolean);

        const subscriptionData = await contactService.contacts.getBulkSubscriptionStatuses(allEmails, allPhones);

        const processedContacts = rawContacts.map((contact: ContactListItem) => ({
          ...contact,
          labels: contact.labels || [],
          labelIds: contact.labelIds || [],
          displayName: contact.displayName ||
            `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
            (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : tr('pages.bookingSystem.ContactsList.unknownContact')),
          emailSubscribed: contact.primaryEmail ? subscriptionData.emailSubscriptions.get(contact.primaryEmail) || false : false,
          smsSubscribed: contact.primaryPhone ? subscriptionData.smsSubscriptions.get(contact.primaryPhone) || false : false,
        }));

        localStorage.setItem('cachedContacts', JSON.stringify(processedContacts));
        localStorage.setItem('contactsLastLoadTime', Date.now().toString());
        setContacts(processedContacts);
      }

      if (labelsResult.status === 'fulfilled') {
        localStorage.setItem('cachedLabels', JSON.stringify(labelsResult.value));
        setAvailableLabels(labelsResult.value);
      }
    } catch (error) {
      console.error('Error refreshing contacts:', error);
    } finally {
      setIsLoading(false); // Add this to hide skeleton
      event.detail.complete();
    }
  };


  const handleCall = (phone: string, e: React.MouseEvent<HTMLIonItemOptionElement, MouseEvent>): void => {
    e.preventDefault();
    if (phone) {
      void placePhoneCall(phone);
    }
  };

  const handleDeleteClick = async (contact: ContactListItem): Promise<void> => {
    if (!contact.id) {
      console.error('Cannot delete contact without an ID');
      alert(tr('pages.bookingSystem.ContactsList.failedToDeleteContactNoId'));
      return;
    }

    if (window.confirm(tr('pages.bookingSystem.ContactsList.areYouSureYouWantTo', { firstName: contact.firstName, lastName: contact.lastName }))) {
      try {
        await contactService.contacts.deleteContact(contact.id);
        const updatedContacts = contacts.filter((c: ContactListItem) => c.id !== contact.id);
        // Maintain sorted order after deletion
        setContacts(sortContacts(updatedContacts));

        localStorage.setItem('cachedContacts', JSON.stringify(updatedContacts));
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert(tr('pages.bookingSystem.ContactsList.failedToDeleteContactPleaseTry'));
      }
    }
  };

  const handleMessage = (phone: string, e: React.MouseEvent<HTMLIonItemOptionElement, MouseEvent>): void => {
    e.preventDefault();
    if (phone) {
      window.location.href = `sms:${phone}`;
    }
  };

  const handleEmail = (email: string, e: React.MouseEvent<HTMLIonItemOptionElement, MouseEvent>): void => {
    e.preventDefault();
    if (email) {
      window.location.href = `mailto:${email}`;
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    setActiveFilter('all');
    setSelectedLabelId('');
    setFilteredContacts(contacts);
    if (isFilterModalOpen) {
      setIsFilterModalOpen(false);
    }
  };

  const handleToggleSearch = () => {
    if (isSearchVisible) {
      // Clear input immediately
      if (searchRef.current) {
        try { (searchRef.current as HTMLIonSearchbarElement).value = ''; } catch { }
      }
      setSearchText('');
      setFilteredContacts(contacts);
      // Start closing animation
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => {
        setIsSearchHidden(true);
        setIsSearchClosing(false);
      }, 220); // Match animation duration

      // Blur input and hide keyboard
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (getter) {
          getter().then((inputEl: HTMLInputElement) => {
            try { inputEl.blur(); } catch { }
            try { Keyboard.hide(); } catch { }
          }).catch(() => { try { Keyboard.hide(); } catch { } });
        } else {
          try { Keyboard.hide(); } catch { }
        }
      } catch { }
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);

      // Focus input after expansion animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateOverlayPosition();
          try {
            const s = searchRef.current as HTMLIonSearchbarElement | null;
            const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
            if (getter) {
              getter().then((inputEl: HTMLInputElement) => {
                try { inputEl.focus(); } catch { }
              }).catch(() => { });
            }
          } catch { }
        });
      });
    }
  };

  const getFilterDisplayName = (filterType: string): string => {
    if (filterType === 'all') return tr('pages.bookingSystem.ContactsList.allContacts');
    if (filterType === 'recent') return tr('pages.bookingSystem.ContactsList.recentContacts');
    if (filterType === 'label' && selectedLabelId) {
      const label = availableLabels.find(l => l.id === selectedLabelId);
      return label ? label.name : tr('pages.bookingSystem.ContactsList.selectedLabel');
    }
    return tr('pages.bookingSystem.ContactsList.allContacts');
  };

  // Render filter modal options as JSX here
  const filterMenuOptions = getFilterOptions();

  // Group contacts into letter-based buckets so the list renders as
  // separate cards per letter instead of one continuous list.
  const getGroupLetter = (contact: ContactListItem): string => {
    const name = contact.displayName ||
      `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
      (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : '');
    const first = name.trim().charAt(0).toUpperCase();
    return /[A-Z]/.test(first) ? first : '#';
  };

  // Bucket by letter with a map (not a contiguous-run scan) so grouping is
  // correct even if the upstream sort key differs from displayName (e.g.
  // sorted by firstName/lastName while grouped by displayName/company name).
  const visibleContacts = filteredContacts.slice(0, visibleCount);
  const groupMap = new Map<string, ContactListItem[]>();
  visibleContacts.forEach(contact => {
    const letter = getGroupLetter(contact);
    const bucket = groupMap.get(letter);
    if (bucket) {
      bucket.push(contact);
    } else {
      groupMap.set(letter, [contact]);
    }
  });
  const getDisplayNameForSort = (contact: ContactListItem): string =>
    contact.displayName ||
    `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
    (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : '');

  const contactGroups = Array.from(groupMap.entries())
    .map(([letter, items]) => ({
      letter,
      items: [...items].sort((a, b) => getDisplayNameForSort(a).localeCompare(getDisplayNameForSort(b))),
    }))
    .sort((a, b) => {
      if (a.letter === '#') return 1;
      if (b.letter === '#') return -1;
      return a.letter.localeCompare(b.letter);
    });

  return (
    <>
      <SheetModal
        isOpen={isFilterModalOpen}
        onClose={() => {
          setIsFilterModalOpen(false);
        }}
        title={tr('pages.bookingSystem.ContactsList.filterContacts')}
        showChips={true}
        menuOptions={filterMenuOptions.map(option => ({
          ...option,
          labelContent: (
            <IonItem lines="none" className={styles.filterItem}>
              <IonLabel>{option.label}</IonLabel>
              <IonChip color={option.chipColor} slot="end">
                <IonLabel>{option.count}</IonLabel>
              </IonChip>
            </IonItem>
          )
        }))}
      />
      <IonPage className={styles.page}>
        <BasePageWithHeader
          ref={baseHeaderRef}
          title={tr('pages.bookingSystem.ContactsList.contacts')}
          toolbarClassName={styles.toolbar}
          smallTitleClassName={styles.smallTitle}
          titleHeaderClassName={styles.titleHeader}
          startButtons={
            <>
              <IonBackButton
              defaultHref='/tabs/dashboard'
              type="button"
              className={styles.backButton}
              icon={chevronBack} />
              <IonButton
                shape="round"
                disabled
                style={{ visibility: 'hidden', pointerEvents: 'none' }}
              >
                <IonIcon icon={filter} />
              </IonButton>
            </>
          }
          endButtons={
            <>
              <IonButton
                shape="round"
                className={styles.searchButton}
                onClick={handleToggleSearch}
              >
                <IonIcon icon={isSearchVisible ? closeOutline : searchOutline} />
              </IonButton>
              <IonButton
                shape="round"
                className={styles.filterButton}
                onClick={handleFilterClick}
              >
                <IonIcon icon={filter} />
              </IonButton>
            </>
          }
        >
          <IonContent scrollY={false} className={styles.content}>
            <PositionedRefresher
              titleRef={titleCardElementRef}
              contentRef={contentElementRef}
              offset={20}
              anchor="bottom"
              id="contacts-refresher"
              className={styles.contactsRefresher}
              pullMin={120}
              pullMax={200}
              pullFactor={0.6}
              disabled={refresherDisabled}
              onIonRefresh={handleRefresh}
              style={{
                '--ion-color-step-250': 'var(--ion-background-color)',
                '--ion-color-step-200': 'var(--ion-background-color)',
                '--ion-background-color': 'transparent',
              } as React.CSSProperties}
            >
              <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
            </PositionedRefresher>
            <IonGrid
              key={scrollResetKey}
              ref={contentElementRef}
              className={styles.contactsContainer}
              onScroll={(e) => {
                const el = e.target as HTMLElement;
                const top = el.scrollTop || 0;
                const atTop = top <= 5;
                if (atTop !== wasAtTop.current) {
                  wasAtTop.current = atTop;
                  setRefresherDisabled(!atTop);
                }
                handleScroll(top);
                const deltaFromBottom = el.scrollHeight - top - el.clientHeight;
                if (deltaFromBottom < 200) {
                  setVisibleCount(v => v < filteredContacts.length ? v + 50 : v);
                }
              }}
            >
              <div ref={titleCardElementRef}>
                <TitleCard
                  ref={titleCardAnimationRef}
                  title={tr('pages.bookingSystem.ContactsList.contacts')}
                  cardClassName={styles.contactsTitleCard}
                  titleContainerClassName={styles.titleContainer}
                  titleClassName={styles.contactsTitle}
                />
              </div>
              {isLoading ? (
                <IonList className={`${styles.contactList} ${styles.skeletonOverlay}`}>
                  {Array.from({ length: 15 }, (_, index) => (
                    <IonItem key={index} lines="full">
                      <IonSkeletonText animated slot="start" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                      <IonLabel>
                        <h3><IonSkeletonText animated style={{ width: '70%', height: '20px' }} /></h3>
                      </IonLabel>
                      <IonSkeletonText animated slot="end" style={{ width: '60px', height: '20px' }} />
                    </IonItem>
                  ))}
                </IonList>
              ) : contacts.length === 0 && hasLoadedOnce ? (
                <IonGrid className={styles.emptyState}>
                  <IonRow>
                    <IonCol className="ion-text-center">
                      <IonList role="list">
                        <IonItem lines="none" className="ion-text-center" role="listitem">
                          <IonLabel color="medium" className="ion-text-wrap">
                            <IonText className="ion-text-large">{tr('pages.bookingSystem.ContactsList.noContactsFound')}
                            </IonText>
                          </IonLabel>
                        </IonItem>
                        <IonItem lines="none" className="ion-text-center" role="listitem">
                          <IonLabel color="medium" className="ion-text-wrap">
                            <IonText>{tr('pages.bookingSystem.ContactsList.tryAddingANewContact')}</IonText>
                          </IonLabel>
                        </IonItem>
                      </IonList>
                      <IonButton onClick={handleAddContact}>{tr('pages.bookingSystem.ContactsList.addContact')}
                      </IonButton>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              ) : contacts.length > 0 && filteredContacts.length === 0 && hasLoadedOnce ? (
                <IonGrid className={styles.emptyState}>
                  <IonRow>
                    <IonCol className="ion-text-center">
                      <IonList role="list">
                        <IonItem lines="none" className="ion-text-center" role="listitem">
                          <IonLabel color="medium" className="ion-text-wrap">
                            <IonText className="ion-text-large">{tr('pages.bookingSystem.ContactsList.noContactsFound')}
                              {searchText && (<span>{tr('pages.bookingSystem.ContactsList.for', { searchText })}</span>)}
                              {activeFilter !== 'all' && (<span>{tr('pages.bookingSystem.ContactsList.in')} {getFilterDisplayName(activeFilter)}</span>)}
                            </IonText>
                          </IonLabel>
                        </IonItem>
                        <IonItem lines="none" className="ion-text-center" role="listitem">
                          <IonLabel color="medium" className="ion-text-wrap">
                            <IonText>{tr('pages.bookingSystem.ContactsList.tryAdjustingYourSearchOrFilters')}</IonText>
                          </IonLabel>
                        </IonItem>
                      </IonList>
                      {(searchText || activeFilter !== 'all') && (
                        <IonButton onClick={handleClearSearch}>{tr('pages.bookingSystem.ContactsList.clearSearch')}
                        </IonButton>
                      )}
                    </IonCol>
                  </IonRow>
                </IonGrid>
              ) : filteredContacts.length > 0 ? (
                <div className={styles.letterGroups}>
                  {contactGroups.map((group) => (
                    <div key={group.letter} className={styles.letterGroup}>
                      <IonText className={styles.letterHeading}>{group.letter}</IonText>
                      <IonList className={styles.contactList}>
                        {group.items.map(contact => (
                          <IonItemSliding
                            key={contact.id}
                          >
                            <IonItemOptions side="start">
                              <IonItemOption color="danger" expandable onClick={() => handleDeleteClick(contact)} className={styles.smallOptionText}>
                                <IonIcon slot="bottom" icon={trash}></IonIcon>{tr('pages.bookingSystem.ContactsList.delete')}
                              </IonItemOption>
                            </IonItemOptions>

                            <IonItem button detail={false} onClick={() => handleContactClick(contact)} className={styles.solidItem} role="listitem">
                              <IonAvatar slot="start" className={styles.avatar}>
                                <img
                                  src={proxyImage(contact.avatar ?? '') || getDefaultAvatar(
                                    contact.displayName ||
                                    `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
                                    (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : 'Contact')
                                  )}
                                  alt={contact.displayName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.primaryEmail || tr('pages.bookingSystem.ContactsList.contact')}
                                  onError={(e) => {
                                    const imgElement = e.target as HTMLImageElement;

                                    imgElement.src = getDefaultAvatar(
                                      contact.displayName ||
                                      `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
                                      (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : 'Contact')
                                    );
                                  }}
                                />
                              </IonAvatar>

                              <IonLabel>
                                {contact.displayName ||
                                  (contact.firstName || contact.lastName ?
                                    `${contact.firstName || ''} ${contact.lastName || ''}`.trim() :
                                    (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : tr('pages.bookingSystem.ContactsList.unknownContact'))
                                  )
                                }
                              </IonLabel>
                              {(() => {
                                const { clientType } = contact.contactfilter?.[0] || {};
                                // Only show chip if clientType contains "members"
                                return Array.isArray(clientType) && clientType.includes('members') && (
                                  <IonChip className={styles.memberChip} slot="end">
                                    <IonLabel>{tr('pages.bookingSystem.ContactsList.member')}</IonLabel>
                                  </IonChip>
                                );
                              })()}
                              <IonIcon
                                slot="end"
                                icon={swapHorizontal}
                                color="medium"
                                size="small"
                                className={styles.slideIndicator}
                                aria-label={tr('pages.bookingSystem.ContactsList.swipeForOptions')}
                              />
                            </IonItem>

                            <IonItemOptions side="end">
                              <IonItemOption color="primary" onClick={(e) => handleCall(contact.primaryPhone, e)} className={styles.smallOptionText}>
                                <IonIcon slot="bottom" icon={call}></IonIcon>{tr('pages.bookingSystem.ContactsList.call')}
                              </IonItemOption>
                              <IonItemOption color="secondary" onClick={(e) => handleMessage(contact.primaryPhone, e)} className={styles.smallOptionText}>
                                <IonIcon slot="bottom" icon={chatbubble}></IonIcon>{tr('pages.bookingSystem.ContactsList.message')}
                              </IonItemOption>
                              <IonItemOption color="tertiary" expandable onClick={(e) => handleEmail(contact.primaryEmail, e)} className={styles.smallOptionText}>
                                <IonIcon slot="bottom" icon={mail}></IonIcon>{tr('pages.bookingSystem.ContactsList.email')}
                              </IonItemOption>
                            </IonItemOptions>
                          </IonItemSliding>
                        ))}
                      </IonList>
                    </div>
                  ))}
                </div>
              ) : null}
            </IonGrid>
          </IonContent>

          {/* Keep FAB and search overlay outside of IonContent so they stay fixed to the viewport
             and do not get clipped or hidden when the WebView resizes for the soft keyboard. */}
          <IonFab ref={el => { fabRef.current = el as unknown as HTMLElement; }} className={styles.calendarFab}>
            <IonFabButton
              size='small'
              onClick={() => {
                if (isSearchVisible) {
                  handleToggleSearch();
                } else {
                  handleAddContact();
                }
              }}
              aria-label={isSearchVisible ? tr('pages.bookingSystem.ContactsList.closeSearch') : tr('pages.bookingSystem.ContactsList.addNewContact')}
            >
              <IonIcon icon={isSearchVisible ? closeOutline : personAddOutline} />
            </IonFabButton>
          </IonFab>

          {/* Non-invasive overlay: centered pop-out; does NOT change FAB styles */}
          <IonFab ref={el => { overlayRef.current = el as unknown as HTMLElement; }} className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`} aria-hidden={isSearchVisible ? 'false' : 'true'}>
            <IonSearchbar
              ref={el => { searchRef.current = el as unknown as HTMLIonSearchbarElement | null; }}
              value={searchText}
              debounce={50}
              onIonFocus={() => {
                requestAnimationFrame(() => updateOverlayPosition());
              }}
              onIonInput={e => setSearchText(e.detail.value ?? '')}
              onIonClear={() => {
                // Clear the search text and reset filtered results immediately.
                setFilteredContacts(contacts);
                setSearchText('');
                setActiveFilter('all');
                setSelectedLabelId('');
              }}
              placeholder={tr('pages.bookingSystem.ContactsList.searchContacts', { length: contacts.length })}
              animated
              showCancelButton="never"
              showClearButton={searchText ? 'always' : 'never'}
              className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
            />
          </IonFab>
        </BasePageWithHeader>

        <IonModal
          isOpen={showNewContactModal}
          onDidDismiss={() => setShowNewContactModal(false)}
          onWillDismiss={() => setShowNewContactModal(false)}
          canDismiss={true}
          backdropDismiss={true}
          keepContentsMounted={false}
          handle={false}
          className={styles.fullscreenModal}
        >
          <NewContact onClose={() => setShowNewContactModal(false)} />
        </IonModal>
      </IonPage>
    </>
  );
};

export default ContactsList;

